/**
 * ShelfMapEligibilityGate – standardised eligibility guard and no-map fallback UI.
 *
 * Issue: #227 – CORE – Warehouse Shelf Map – Map Eligibility Guardrails & No-Location Fallback
 *
 * Wraps a shelf-map launcher or action button.
 * Before opening the map the gate:
 *   1. Calls POST /api/rest/app/v1/shelf-map/eligibility.
 *   2. If eligible → calls onEligible() to open the viewer.
 *   3. If ineligible → displays a user-friendly Alert with the reason message
 *      (no hard errors, no empty states, no map is launched).
 *
 * Reason codes and their user-facing messages are owned by the backend
 * (REASON_MESSAGES in shelf-map-eligibility.ts) and returned as `message`
 * in the eligibility response. The component just renders what the server says.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close, Map as MapIcon } from '@mui/icons-material';
import { useShelfMapEligibility } from '@/hooks/queries/shelf-map/useShelfMapEligibility';
import type {
  ShelfMapEligibilityInput,
  ShelfMapIneligibilityReason,
} from '@/services/shelfMapService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShelfMapEligibilityGateProps {
  /** Input to POST /api/rest/app/v1/shelf-map/eligibility. */
  eligibilityInput: ShelfMapEligibilityInput;
  /** Called when the eligibility check passes. Open the viewer here. */
  onEligible: () => void;
  /** Render prop for the trigger button. Receives isChecking for spinner. */
  renderTrigger: (props: { onClick: () => void; isChecking: boolean }) => React.ReactNode;
  /** Optional title override for the ineligibility dialog. */
  ineligibilityDialogTitle?: string;
}

// ---------------------------------------------------------------------------
// Severity by reason
// ---------------------------------------------------------------------------

const reasonSeverity = (reason?: ShelfMapIneligibilityReason): 'info' | 'warning' | 'error' => {
  if (!reason) return 'info';
  if (reason === 'no-primary-secondary') return 'info';
  if (reason === 'no-map-config' || reason === 'no-template-binding') return 'warning';
  return 'info';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Wraps a map launcher to run an eligibility check before opening the viewer.
 * Ineligible responses are shown as an in-place alert rather than an error.
 */
export const ShelfMapEligibilityGate: React.FC<ShelfMapEligibilityGateProps> = ({
  eligibilityInput,
  onEligible,
  renderTrigger,
  ineligibilityDialogTitle = 'Shelf Map Unavailable',
}) => {
  const { isChecking, result, checkEligibility, reset } = useShelfMapEligibility();
  const [showDialog, setShowDialog] = useState(false);

  const handleClick = async () => {
    reset();
    const res = await checkEligibility(eligibilityInput);
    if (res.eligible) {
      onEligible();
    } else {
      setShowDialog(true);
    }
  };

  const handleClose = () => {
    setShowDialog(false);
    reset();
  };

  return (
    <>
      {renderTrigger({ onClick: () => void handleClick(), isChecking })}

      {/* Ineligibility dialog */}
      <Dialog open={showDialog} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <MapIcon color="disabled" fontSize="small" />
          {ineligibilityDialogTitle}
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {result && !result.eligible && (
            <Alert severity={reasonSeverity(result.reason)} sx={{ mb: 1 }}>
              {result.message ?? 'This item cannot be shown on a shelf map.'}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            Reason code: {result?.reason ?? 'unknown'}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Inline alert variant (for panels that prefer inline over dialog)
// ---------------------------------------------------------------------------

export interface ShelfMapIneligibilityAlertProps {
  message: string;
  reason?: ShelfMapIneligibilityReason;
  onDismiss?: () => void;
}

/**
 * Inline alert for ineligible shelf-map states.
 * Suitable for embedding inside drawers or panels rather than a full dialog.
 */
export const ShelfMapIneligibilityAlert: React.FC<ShelfMapIneligibilityAlertProps> = ({
  message,
  reason,
  onDismiss,
}) => {
  const [visible, setVisible] = useState(true);

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  return (
    <Collapse in={visible}>
      <Box sx={{ my: 1 }}>
        <Alert
          severity={reasonSeverity(reason)}
          onClose={onDismiss !== undefined ? handleDismiss : undefined}
        >
          {message}
        </Alert>
      </Box>
    </Collapse>
  );
};

// ---------------------------------------------------------------------------
// Eligibility-gated launcher button (convenience wrapper)
// ---------------------------------------------------------------------------

export interface EligibilityGatedMapButtonProps {
  label?: string;
  eligibilityInput: ShelfMapEligibilityInput;
  onEligible: () => void;
  disabled?: boolean;
}

/**
 * A "View on Shelf Map" button that runs eligibility check before opening the map.
 * Shows a spinner during the check and a dialog on failure.
 */
export const EligibilityGatedMapButton: React.FC<EligibilityGatedMapButtonProps> = ({
  label = 'View on Shelf Map',
  eligibilityInput,
  onEligible,
  disabled = false,
}) => (
  <ShelfMapEligibilityGate
    eligibilityInput={eligibilityInput}
    onEligible={onEligible}
    renderTrigger={({ onClick, isChecking }) => (
      <Button
        variant="outlined"
        size="small"
        startIcon={isChecking ? <CircularProgress size={14} /> : <MapIcon />}
        onClick={onClick}
        disabled={disabled || isChecking}
        aria-label={label}
      >
        {label}
      </Button>
    )}
  />
);
